import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Gift, DollarSign, Wrench, HandHelping, AlertTriangle, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Activity() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);

  const categories = [
    { id: 'free', name: 'Free', icon: Gift, color: 'bg-green-500', description: 'Free items & giveaways' },
    { id: 'sale', name: 'Sale', icon: DollarSign, color: 'bg-blue-500', description: 'Items for sale' },
    { id: 'service', name: 'Service', icon: Wrench, color: 'bg-purple-500', description: 'Services offered' },
    { id: 'help', name: 'Help', icon: HandHelping, color: 'bg-orange-500', description: 'Need or offering help' },
    { id: 'emergency', name: 'Emergency', icon: AlertTriangle, color: 'bg-red-500', description: 'Urgent requests' },
    { id: 'network', name: 'Network', icon: Users, color: 'bg-pink-500', description: 'Networking & community' },
  ];

  const handleCategoryClick = async (categoryId) => {
    setSelectedCategory(categoryId);
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const userLocation = JSON.parse(localStorage.getItem('userLocation') || '{"lat": 12.9716, "lng": 77.5946}');
      
      const response = await axios.get(`${API}/posts`, {
        params: {
          latitude: userLocation.lat,
          longitude: userLocation.lng,
          radius: 10000,
          category: categoryId
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="flex items-center gap-4 px-4 py-4">
          <button onClick={() => navigate('/home')}>
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Activity Categories</h1>
        </div>
      </header>

      {/* Categories Grid */}
      <main className="p-4">
        <div className="grid grid-cols-2 gap-4 mb-6">
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => handleCategoryClick(category.id)}
                className={`p-6 rounded-2xl shadow-sm border-2 transition ${
                  selectedCategory === category.id
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className={`w-12 h-12 ${category.color} rounded-full flex items-center justify-center mx-auto mb-3`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-gray-900 text-center mb-1">{category.name}</h3>
                <p className="text-xs text-gray-600 text-center">{category.description}</p>
              </button>
            );
          })}
        </div>

        {/* Results */}
        {selectedCategory && (
          <div className="mt-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              {categories.find(c => c.id === selectedCategory)?.name} Posts
            </h2>
            
            {loading ? (
              <div className="text-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto"></div>
              </div>
            ) : posts.length === 0 ? (
              <p className="text-gray-500 text-center py-10">No posts in this category</p>
            ) : (
              <div className="space-y-3">
                {posts.map((post) => (
                  <div
                    key={post.id}
                    onClick={() => navigate(`/post/${post.id}`)}
                    className="bg-white p-4 rounded-xl border border-gray-200 cursor-pointer hover:shadow-md transition"
                  >
                    <h4 className="font-bold text-gray-900 mb-1">{post.title}</h4>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">{post.description}</p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{post.user_name}</span>
                      <span>{post.distance} km away</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}